import { motion } from 'motion/react';
import { ScanFace, Linkedin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { translations } from '../translations';
import SEO from '../components/SEO';
import { getLocalizedPath } from '../lib/i18n';

import { TechStackMarquee } from '../components/TechStackMarquee';
import { TabidachiLogo } from '../components/TabidachiLogo';

interface HomeProps {
  lang: string;
}

interface Post {
  id: string;
  title: string;
  coverImage: string;
  categories: string[];
  slug: string;
  featured?: boolean;
  published: boolean;
}

export default function Home({ lang }: HomeProps) {
  const t = translations[lang];
  const navigate = useNavigate();

  const l = (path: string) => getLocalizedPath(path, lang);

  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Faizi Zahari",
    "url": "https://faizizahari.com",
    "sameAs": [
      "https://www.linkedin.com/in/faizizahari/",
      "https://github.com/faizizahari",
      "https://threads.net/@faizizahari"
    ],
    "jobTitle": "Growth Marketing & Product Lead",
    "worksFor": {
      "@type": "Organization",
      "name": "WorkMagic"
    },
    "description": "Growth Marketing & Product Lead at WorkMagic. Building products people love and scaling companies through data-driven strategies.",
    "image": "https://framerusercontent.com/images/UBzrg9GJkgCEFxMeW7M5DjM.jpeg"
  };

  return (
    <>
      <SEO 
        title="Home" 
        description="Growth Marketing & Product Lead at WorkMagic. I build products that people love and help companies scale through data-driven strategies."
        jsonLd={personSchema}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
      {/* Card 1: Intro */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        onClick={() => navigate(l('/about'))}
        className="bg-white rounded-[32px] p-8 md:p-10 flex flex-col justify-between col-span-1 md:col-span-2 min-h-[300px] relative overflow-hidden shadow-[0_2px_10px_rgba(0,0,0,0.04)] border border-gray-100 group cursor-pointer"
      >
        <div className="flex justify-between items-start relative z-10">
          <img 
            src="/avatar.jpg" 
            alt="Faizi" 
            className="w-24 h-24 rounded-full border-4 border-white shadow-sm object-cover bg-slate-100" 
            referrerPolicy="no-referrer"
            loading="eager"
          />
        </div>
        <div className="w-48 h-48 bg-[#efe3fc] rounded-full flex items-center justify-center absolute -top-8 -right-8 group-hover:scale-110 transition-transform duration-500">
          <img 
            src="https://framerusercontent.com/images/sv8Y9tO8bKncNZEJtQfNnMImG4.png?scale-down-to=256&format=webp&quality=50" 
            srcSet="https://framerusercontent.com/images/sv8Y9tO8bKncNZEJtQfNnMImG4.png?scale-down-to=128&format=webp&quality=50 128w, https://framerusercontent.com/images/sv8Y9tO8bKncNZEJtQfNnMImG4.png?scale-down-to=256&format=webp&quality=50 256w"
            sizes="128px"
            alt="WorkMagic Logo" 
            className="w-32 h-32 object-contain opacity-20"
            referrerPolicy="no-referrer"
            loading="eager"
          />
        </div>
        <div className="mt-12 relative z-10">
          <h1 className="text-[2.5rem] font-bold mb-2 leading-tight text-[#323232]">{t.introTitle}</h1>
          <p className="text-gray-500 text-xl font-medium max-w-sm">{t.introSub}</p>
        </div>
      </motion.div>

      {/* Card 2: Resume */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        onClick={() => navigate(l('/resume'))}
        className="bg-white rounded-[32px] p-8 md:p-10 flex flex-col justify-between col-span-1 min-h-[300px] shadow-[0_2px_10px_rgba(0,0,0,0.04)] border border-gray-100 group cursor-pointer"
      >
        <div className="w-16 h-16 text-[#f1b43c] mb-4 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
          <ScanFace size={48} strokeWidth={1.5} />
        </div>
        <div>
          <p className="text-gray-500 text-base font-medium mb-1">{t.learnMore}</p>
          <h2 className="text-[2rem] font-bold text-[#323232] leading-tight">{t.myResume}</h2>
        </div>
      </motion.div>

      {/* Card 3: TikTok */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        onClick={() => navigate(l('/portfolio/tiktok-ads-manager'))}
        className="bg-gray-100 rounded-[32px] p-8 flex flex-col justify-end col-span-1 min-h-[300px] relative overflow-hidden group cursor-pointer shadow-[0_2px_10px_rgba(0,0,0,0.04)] border border-gray-100"
      >
        <img 
          src="https://framerusercontent.com/images/zDsEVnHYXNQThYxljBcebq8MJQ.jpg?scale-down-to=384&format=webp&quality=30" 
          srcSet="https://framerusercontent.com/images/zDsEVnHYXNQThYxljBcebq8MJQ.jpg?scale-down-to=384&format=webp&quality=30 384w, https://framerusercontent.com/images/zDsEVnHYXNQThYxljBcebq8MJQ.jpg?scale-down-to=768&format=webp&quality=30 768w"
          sizes="(max-width: 768px) 100vw, 384px"
          alt="TikTok" 
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" 
          referrerPolicy="no-referrer"
          fetchPriority="high"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="relative z-10 text-white">
          <p className="text-sm font-bold uppercase tracking-wider mb-1">{t.tiktok}</p>
          <h2 className="text-2xl font-bold leading-tight">{t.tiktokTitle}</h2>
        </div>
      </motion.div>

      {/* Card 4: Tabidachi */}
      <motion.a 
        href="https://tabidachi.io/"
        target="_blank"
        rel="noopener noreferrer"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="bg-white hover:bg-[#FFB7C5] transition-colors duration-500 rounded-[32px] p-8 flex flex-col items-center justify-center col-span-1 min-h-[300px] shadow-[0_2px_10px_rgba(0,0,0,0.04)] border border-gray-100 group cursor-pointer"
        aria-label="Tabidachi Website"
      >
        <div className="relative group-hover:scale-105 transition-transform duration-300">
          <TabidachiLogo size={160} />
        </div>
      </motion.a>

      {/* Card 5: LinkedIn */}
      <motion.a 
        href="https://www.linkedin.com/in/faizizahari/"
        target="_blank"
        rel="noopener noreferrer"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="bg-white rounded-[32px] p-8 flex flex-col items-center justify-center col-span-1 min-h-[300px] shadow-[0_2px_10px_rgba(0,0,0,0.04)] border border-gray-100 cursor-pointer group hover:bg-[#0A66C2] transition-colors duration-500"
        aria-label="LinkedIn Profile"
      >
        <div className="bg-gray-500 text-white p-5 rounded-2xl group-hover:bg-white group-hover:text-[#0A66C2] transition-colors duration-500 shadow-sm">
          <Linkedin size={80} strokeWidth={1.5} />
        </div>
      </motion.a>

      {/* Card 6: Tech Stack */}
      <TechStackMarquee 
        title={t.techStack}
        className="col-span-1 md:col-span-2 min-h-[300px]"
        onClick={() => navigate(l('/skillset#tools-tech'))}
      />

      {/* Card 7: Contact */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
        onClick={() => navigate(l('/contact'))}
        className="bg-white rounded-[32px] p-8 md:p-10 flex flex-col justify-end col-span-1 md:col-span-2 min-h-[300px] shadow-[0_2px_10px_rgba(0,0,0,0.04)] border border-gray-100 cursor-pointer group"
      >
        <h2 className="text-[2.5rem] font-bold mb-1 text-[#323232] leading-tight">{t.workTogether}</h2>
        <p className="text-[2.5rem] font-bold text-[#b45309] group-hover:translate-x-2 transition-transform duration-300">{t.getInTouchNow}</p>
      </motion.div>

      {/* Card 8: Featured Blog Post */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.7 }}
        onClick={() => navigate(l(`/blog/inside-pokémon-s-monetization-engine-strategy`))}
        className="bg-white rounded-[32px] p-8 md:p-10 flex flex-col justify-end col-span-1 md:col-span-2 min-h-[300px] relative overflow-hidden group cursor-pointer shadow-[0_2px_10px_rgba(0,0,0,0.04)] border border-gray-100"
      >
        <img 
          src="https://framerusercontent.com/images/DF3TboCD5DzknPWyIWPFk33jc.png?scale-down-to=512&format=webp&quality=30" 
          srcSet="https://framerusercontent.com/images/DF3TboCD5DzknPWyIWPFk33jc.png?scale-down-to=512&format=webp&quality=30 512w, https://framerusercontent.com/images/DF3TboCD5DzknPWyIWPFk33jc.png?scale-down-to=1024&format=webp&quality=30 1024w"
          sizes="(max-width: 768px) 100vw, 512px"
          alt="Featured Article" 
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" 
          referrerPolicy="no-referrer"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        <div className="relative z-10 text-white drop-shadow-md">
          <div className="flex flex-wrap gap-2 mb-2">
            <span className="text-sm font-bold uppercase tracking-wider hover:text-[#f1b43c] transition-colors cursor-pointer">
              {t.growth}
            </span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold leading-tight line-clamp-2 min-h-[3.5rem] md:min-h-[4.5rem]">
            {t.pokemonTitle}
          </h2>
        </div>
      </motion.div>
    </div>
    </>
  );
}
